// app/wishlist/[schoolID]/page.jsx
// ----------------------------------
// Full detail page for a teacher's wishlist.
// Route: /wishlist/[schoolID]

"use client";
import React from 'react';
import { useEffect, useState } from "react";
import Link from "next/link";

const CATEGORY_EMOJI = {
  "Art Supplies":             "🎨",
  "Books":                    "📚",
  "Classroom Basics":         "🏫",
  "Educational Games":        "🎮",
  "Food, Clothing & Hygiene": "🍎",
  "Instructional Technology": "🔌",
  "STEM":                     "🔬",
  "Decor":                    "🌟",
  "Furniture":                "🪑",
  "Other":                    "📦",
}

// const CATEGORY_COLORS = {
//   "Books":        { bg: "#eff6ff", text: "#3b82f6" },
//   "Supplies":     { bg: "#fefce8", text: "#ca8a04" },
//   "Art Supplies": { bg: "#fff7ed", text: "#f97316" },
//   "Decor":        { bg: "#fdf4ff", text: "#a855f7" },
//   "Furniture":    { bg: "#f0fdf4", text: "#16a34a" },
//   "Instructional Technology":  { bg: "#f0f9ff", text: "#0284c7" },
//   "Classroom Basics":    { bg: "#fff1f2", text: "#e11d48" },
//   "Other":        { bg: "#f8fafc", text: "#64748b" },
// };

// // ── Category pill ────────────────────────────────────────────────────────────
// function CategoryPill({ category }) {
//   const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS["Other"];
//   return (
//     <span style={{
//       fontSize:     11,
//       fontWeight:   600,
//       padding:      "3px 10px",
//       borderRadius: 999,
//       background:   colors.bg,
//       color:        colors.text,
//       whiteSpace:   "nowrap",
//     }}>
//       {CATEGORY_EMOJI[category] || "📦"} {category}
//     </span>
//   );
// }

// ── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ value, label, color, prefix = "" }) {
  return (
    <div style={{
      background:   "#fff",
      borderRadius: 16,
      padding:      "20px 24px",
      boxShadow:    "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
      flex:         1,
      minWidth:     120,
    }}>
      <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>
        {prefix}{typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function WishlistDetailPage({ params }) {
  const resolvedParams = React.use(params);
  const schoolID = resolvedParams.schoolID;
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [filterCat,     setFilterCat]     = useState("All");
  const [filterStatus,  setFilterStatus]  = useState("All");  // All | Needed | Purchased
  const [updatingItem,  setUpdatingItem]  = useState(null);

  useEffect(() => {
    fetch(`/api/wishlist/${schoolID}/items`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [schoolID]);

  const togglePurchased = async (itemID, current) => {
    setUpdatingItem(itemID);
    try {
      await fetch(`/api/wishlist/${schoolID}/items`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ itemID, purchased: !current }),
      });
      // Optimistic update
      setData(prev => ({
        ...prev,
        items: prev.items.map(i =>
          i.itemID === itemID ? { ...i, purchased: !current } : i
        ),
        totals: {
          ...prev.totals,
          purchasedCount: prev.totals.purchasedCount + (!current ? 1 : -1),
          unpurchased:    prev.totals.unpurchased    + (!current ? -1 : 1),
        },
      }));
    } catch (e) {
      console.error("Toggle error:", e);
    }
    setUpdatingItem(null);
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#94a3b8" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🎁</div>
        <div style={{ fontSize: 14 }}>Loading wishlist...</div>
      </div>
    </div>
  );

  if (error || !data) return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", color: "#f87171" }}>
        <div style={{ fontSize: 14 }}>Could not load this wishlist.</div>
        <Link href="/" style={{ color: "#3b82f6", fontSize: 13, marginTop: 8, display: "block" }}>← Back to map</Link>
      </div>
    </div>
  );

  const { teacher, items, totals } = data;
  const progressPct = totals.totalItems > 0
    ? Math.round((totals.purchasedCount / totals.totalItems) * 100)
    : 0;

  // All unique categories
  const categories = ["All", ...new Set(items.map(i => i.category || "Other"))];

  // Filtered items
  const filtered = items.filter(i => {
    const catMatch    = filterCat    === "All" || i.category === filterCat;
    const statusMatch = filterStatus === "All"
      || (filterStatus === "Needed"    && !i.purchased)
      || (filterStatus === "Purchased" && i.purchased);
    return catMatch && statusMatch;
  });

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* ── Hero header ── */}
      <div style={{
        background:   "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        padding:      "40px 24px 80px",
        position:     "relative",
        overflow:     "hidden",
      }}>
        {/* Decorative circles */}
        <div style={{ position: "absolute", top: -40,  right: -40,  width: 200, height: 200, borderRadius: "50%", background: "rgba(249,115,22,0.08)" }} />
        <div style={{ position: "absolute", bottom: -60, left: -20, width: 240, height: 240, borderRadius: "50%", background: "rgba(59,130,246,0.06)" }} />

        <div style={{ maxWidth: 900, margin: "0 auto", position: "relative" }}>
          <Link href="/" style={{ color: "#64748b", fontSize: 13, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 24 }}>
            ← Back to map
          </Link>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
            {/* Avatar */}
            <div style={{
              width:          64,
              height:         64,
              borderRadius:   "50%",
              background:     "linear-gradient(135deg, #f97316, #fbbf24)",
              color:          "#fff",
              fontWeight:     800,
              fontSize:       28,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              flexShrink:     0,
              boxShadow:      "0 0 0 4px rgba(249,115,22,0.2)",
            }}>
              {teacher.titleName?.charAt(0)}
            </div>

            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, color: "#fff", margin: 0, lineHeight: 1.2 }}>
                {teacher.titleName} {teacher.lastName}'s Wishlist
              </h1>
              <div style={{ fontSize: 15, color: "#94a3b8", marginTop: 6 }}>
                {teacher.schoolName} · {teacher.address?.city}, {teacher.address?.state} · Grade {teacher.grade}
              </div>

              {/* Progress bar */}
              <div style={{ marginTop: 16, maxWidth: 400 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#64748b", marginBottom: 6 }}>
                  <span>{progressPct}% purchased</span>
                  <span>{totals.purchasedCount} of {totals.totalItems} items</span>
                </div>
                <div style={{ background: "rgba(255,255,255,0.1)", borderRadius: 999, height: 8 }}>
                  <div style={{ background: "linear-gradient(90deg, #10b981, #34d399)", width: `${progressPct}%`, height: 8, borderRadius: 999, transition: "width .5s ease" }} />
                </div>
              </div>
            </div>

            {/* Amazon link */}
            {teacher.wishlistUrl && (
              <a href={teacher.wishlistUrl} target="_blank" rel="noopener noreferrer" style={{
                padding:        "12px 20px",
                background:     "#f97316",
                color:          "#fff",
                borderRadius:   12,
                fontSize:       14,
                fontWeight:     700,
                textDecoration: "none",
                display:        "flex",
                alignItems:     "center",
                gap:            6,
                flexShrink:     0,
                alignSelf:      "flex-start",
                marginTop:      8,
              }}>
                🎁 Donate
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── Stats row (overlapping hero) ── */}
      <div style={{ maxWidth: 900, margin: "-36px auto 0", padding: "0 24px", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <StatCard value={totals.totalItems}     label="Total Items"     color="#f97316" />
          <StatCard value={totals.totalCost}      label="Total Value"     color="#10b981" prefix="$" />
          <StatCard value={totals.unpurchased}    label="Still Needed"    color="#3b82f6" />
          <StatCard value={totals.purchasedCount} label="Purchased"       color="#a855f7" />
        </div>
      </div>

      {/* ── Filters + table ── */}
      <div style={{ maxWidth: 900, margin: "32px auto 60px", padding: "0 24px" }}>

        {/* Filter bar */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
          {/* Status filter */}
          {["All", "Needed", "Purchased"].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding:      "6px 14px",
              borderRadius: 999,
              border:       "none",
              cursor:       "pointer",
              fontSize:     12,
              fontWeight:   600,
              background:   filterStatus === s ? "#1e293b" : "#e2e8f0",
              color:        filterStatus === s ? "#fff"    : "#64748b",
              transition:   "all .15s",
            }}>
              {s === "All" ? "All Items" : s === "Needed" ? "🔵 Still Needed" : "✅ Purchased"}
            </button>
          ))}

          <div style={{ width: 1, height: 20, background: "#e2e8f0", margin: "0 4px" }} />

          {/* Category filter */}
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilterCat(cat)} style={{
              padding:      "6px 14px",
              borderRadius: 999,
              border:       "none",
              cursor:       "pointer",
              fontSize:     12,
              fontWeight:   600,
              background:   filterCat === cat ? "#f97316" : "#e2e8f0",
              color:        filterCat === cat ? "#fff"    : "#64748b",
              transition:   "all .15s",
            }}>
              {cat === "All" ? "All Categories" : `${CATEGORY_EMOJI[cat] || "📦"} ${cat}`}
            </button>
          ))}
        </div>

        {/* Results count */}
        <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 12 }}>
          Showing {filtered.length} of {items.length} items
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                {["#", "Item", "Category", "Price", "Status"].map(h => (
                  <th key={h} style={{
                    padding:    "12px 16px",
                    textAlign:  h === "Price" || h === "Status" ? "center" : "left",
                    fontSize:   11,
                    fontWeight: 700,
                    color:      "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => (
                <tr key={item.itemID} style={{
                  borderBottom: "1px solid #f1f5f9",
                  background:   item.purchased ? "#f0fdf4" : idx % 2 === 0 ? "#fff" : "#fafafa",
                  transition:   "background .15s",
                  opacity:      item.purchased ? 0.75 : 1,
                }}>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#cbd5e1", fontWeight: 600, width: 40 }}>
                    {item.itemID?.replace("ITEM#", "")}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 13, color: "#1e293b", maxWidth: 360 }}>
                    <span style={{ textDecoration: item.purchased ? "line-through" : "none", color: item.purchased ? "#94a3b8" : "#1e293b" }}>
                      {item.title}
                    </span>
                  </td>
                  {/* <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                    <CategoryPill category={item.category || "Other"} />
                  </td> */}
                  <td style={{ padding: "12px 16px", textAlign: "center", fontSize: 13, fontWeight: 700, color: item.price ? "#10b981" : "#cbd5e1", whiteSpace: "nowrap" }}>
                    {item.price ? `$${parseFloat(item.price).toFixed(2)}` : "—"}
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "center" }}>
                    <button
                      onClick={() => togglePurchased(item.itemID, item.purchased)}
                      disabled={updatingItem === item.itemID}
                      style={{
                        padding:      "5px 12px",
                        borderRadius: 999,
                        border:       "none",
                        cursor:       "pointer",
                        fontSize:     11,
                        fontWeight:   700,
                        background:   item.purchased ? "#dcfce7" : "#f1f5f9",
                        color:        item.purchased ? "#16a34a" : "#64748b",
                        transition:   "all .15s",
                        opacity:      updatingItem === item.itemID ? 0.5 : 1,
                      }}
                    >
                      {item.purchased ? "✅ Purchased" : "○ Mark Purchased"}
                    </button>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: "40px", textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
                    No items match your filters.
                  </td>
                </tr>
              )}
            </tbody>

            {/* Totals footer */}
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                  <td colSpan={3} style={{ padding: "12px 16px", fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                    {filtered.length} items shown
                  </td>
                  <td style={{ padding: "12px 16px", textAlign: "center", fontSize: 14, fontWeight: 800, color: "#10b981" }}>
                    ${filtered.reduce((s, i) => s + (parseFloat(i.price) || 0), 0).toFixed(2)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}